import React, {
  Dispatch,
  FC,
  SetStateAction,
  useRef,
  useState,
} from 'react';
import classNames from 'classnames';
import toggleFullScreen from 'utils/toggleFullScreen';
import SpeakerButtons from 'components/VideoPlayer/PlayPauseButtons';
import PlayPauseIcons from 'components/VideoPlayer/PlayPauseIcons';
import PlayPauseBigButtons from 'components/VideoPlayer/PlayPauseBigButtons';
import VideoPopup from 'components/Popup/VideoPopup';
import PromoBanner from 'components/PromoBanner/PromoBanner';
import { ReactComponent as IconFullscreen } from 'assets/images/expand-solid.svg';

import s from './VideoPlayer.module.scss';

export type Props = {
  onPlay(): void,
  className?: string,
  poster: string,
  src: string,
  authorized: boolean,
  flowUrl: string,
  isPlaying: boolean,
  setIsPlaying: Dispatch<SetStateAction<boolean>>
};

const VideoPlayer:FC<Props> = ({
  onPlay,
  poster,
  src,
  className,
  children,
  authorized,
  flowUrl,
  isPlaying,
  setIsPlaying,
}) => {
  const [currentTimePercent, setCurrentTimePercent] = useState(0);
  const [currentTime, setCurrentTime] = useState('00:00');
  const [volume, setVolume] = useState(1);
  const [progressBar, setProgressBar] = useState(0);
  const [isMuted, setIsMuted] = useState(false);
  const [isFullScreen, setIsFullScreen] = useState(false);
  const refProgressBar = useRef<HTMLProgressElement>(null);
  const refPlayerWrapper = useRef<HTMLDivElement>(null);

  const videoRef = useRef<HTMLVideoElement>(null);

  const togglePlay = () => {
    if (!authorized) {
      window.location.href = flowUrl;
      return;
    }

    if (!videoRef.current) return;

    if (videoRef.current.paused) {
      videoRef.current.play();
      setIsPlaying(true);
    } else {
      videoRef.current.pause();
      setIsPlaying(false);
    }
  };

  const seek = (e: { nativeEvent: { offsetX: number; }; }) => {
    if (!videoRef.current) return;
    if (!refProgressBar.current) return;

    const percent = e.nativeEvent.offsetX / refProgressBar.current.offsetWidth;
    setProgressBar(Math.floor(percent * 100));
    if (videoRef.current.duration) {
      videoRef.current.currentTime = percent * videoRef.current.duration;
    } else {
      setCurrentTimePercent(percent);
      togglePlay();
    }
  };

  const updateProgressBar = () => {
    if (!videoRef.current) {
      return;
    }

    const percentage = Math.floor((100 / videoRef.current.duration)
      * videoRef.current.currentTime);
    setProgressBar(percentage);
  };

  const toggleFullScreenBtn = () => {
    toggleFullScreen(isFullScreen, refPlayerWrapper, setIsFullScreen);
  };

  const volumeHandler = (value:string) => {
    if (videoRef.current) {
      setVolume(+value);
      videoRef.current.volume = volume;
      if (+value <= 0) {
        setIsMuted(true);
      } else {
        setIsMuted(false);
      }
    }
  };

  const timeUpdateHandler = () => {
    if (videoRef.current) {
      let S: number | string = videoRef.current.currentTime;
      const m: number | string = Math.floor(S / 60);
      const outputSecondsMinutes = (m >= 10) ? m : `0${m}`;
      S = Math.floor(S % 60);
      const outputSeconds = (S >= 10) ? S : `0${S}`;

      setCurrentTime(`${outputSecondsMinutes}:${outputSeconds}`);
    }
  };

  const loadHandler = () => {
    if (videoRef.current) {
      videoRef.current.currentTime = currentTimePercent * videoRef.current.duration;
    }
  };
  return (
    <>
      <div ref={refPlayerWrapper} className={classNames(s.player, isPlaying ? s.playing : '')}>
        <video
          muted={isMuted}
          ref={videoRef}
          onTimeUpdate={() => { timeUpdateHandler(); updateProgressBar(); }}
          className={classNames(className, s.video, isPlaying ? 'playing' : '')}
          onPlay={onPlay}
          preload="none"
          poster={poster}
          onLoadedData={loadHandler}
        >
          <track
            src="/"
            kind="captions"
            srcLang="ru"
          />
          <source
            type="video/mp4"
            src={src}
          />
        </video>
        {!isPlaying || (
        <div id="videoPlayer-controls" className={s.controls}>
          {/*  eslint-disable */}
          <progress
            className={s.progress}
            onClick={seek}
            ref={refProgressBar}
            // @ts-ignore
            min="0"
            max="100"
            value={progressBar}
          />
          <div className={s.playPause}>
            <PlayPauseIcons isPlaying={isPlaying} onClick={togglePlay} />
          </div>
          <div className={s.time}>
            {currentTime}
          </div>
          <div className={s.controlsBtn}>
            <SpeakerButtons
              isMuted={isMuted}
              setIsMuted={setIsMuted}
            />
            <input
              onChange={(e) => volumeHandler(e.target.value)}
              type="range"
              title="volume"
              min="0"
              max="1"
              step="0.1"
              className={s.volume}
              value={!isMuted ? volume : 0}
            />
            <button
              type="button"
              className={s.btnFullScreen}
              title="toggle full screen"
              onClick={() => toggleFullScreenBtn()}
            >
              <IconFullscreen />
            </button>
          </div>
        </div>
        )}
        {children}
        <PlayPauseBigButtons isPlaying={isPlaying} onClick={togglePlay} />
        {!isPlaying || (
        <>
          <VideoPopup />
          <button type="button" className={s.btnClose} onClick={togglePlay}>
            <span className={s.btnHiddenText}>Toggle play</span>
          </button>
          <PromoBanner className={s.promoBanner} />
        </>
        )}
      </div>
    </>
  );
};

VideoPlayer.defaultProps = {
  className: '',
};

export default VideoPlayer;
